import React from 'react';

import i18n from 'util/i18n';

import { RELATIONSHIPS, RELATIONSHIPS_RELATIVES } from 'config/formSections/relationships';
import { MARRIED, SEPARATED } from 'constants/enums/relationshipOptions';
import ErrorMessageList from 'components/ErrorMessageList';
import { Summary, NameSummary } from 'components/Summary';

import Subsection from 'components/Section/shared/Subsection';
import connectSubsection from 'components/Section/shared/SubsectionConnector';
import { RELATIONSHIPS_STATUS_MARITAL } from 'constants/sections';

import { isPVQForm } from 'helpers/form';
import Field from 'components/Form/Field';
import Accordion from 'components/Form/Accordion';
import Comments from 'components/Form/Comments';
import ErrorMessage from 'components/ErrorMessage';
import { getFilteredParentInfo } from 'helpers/relatives';
import { getDuplicateNamesAndAddresses } from 'helpers/people';
import { relativesAddressNameFields } from 'constants/enums/people';
import Relative from './Relative';

const sectionConfig = {
  key: RELATIONSHIPS_RELATIVES.key,
  section: RELATIONSHIPS.name,
  store: RELATIONSHIPS.store,
  subsection: RELATIONSHIPS_RELATIVES.name,
  storeKey: RELATIONSHIPS_RELATIVES.storeKey,
};

export class Relatives extends Subsection {
  constructor(props) {
    super(props);

    const {
      section, subsection, store, storeKey
    } = sectionConfig;

    this.section = section;
    this.subsection = subsection;
    this.store = store;
    this.storeKey = storeKey;
    this.updateList = this.updateList.bind(this);
    this.summary = this.summary.bind(this);
  }

  // These are warnings so create a work around for applicants
  // that don't know their mother/father.
  getSectionWarnings = () => {
    const { errors = [], maritalStatus } = this.props;
    const errorList = {
      'List.containsRequiredItems.REQUIREMENT_NOT_MET': {
        errors: [
          {
            key: 'relatives-mother-father-required-error',
            title: i18n.t('error.validRelationWarning.title'),
            message: i18n.t('error.validRelationWarning.message'),
            shouldDisplayError: true,
          },
          {
            key: 'relatives-mil-fil-required-error',
            title: i18n.t('error.validMaritalRelationWarning.title'),
            message: i18n.t('error.validMaritalRelationWarning.message'),
            shouldDisplayError: [MARRIED, SEPARATED].includes(maritalStatus),
          },
        ],
      },
    };

    const sectionErrors = [];
    errors.forEach((error) => {
      const errorItem = errorList[error];
      if (errorItem) {
        errorItem.errors.forEach((err) => {
          if (err.shouldDisplayError) {
            sectionErrors.push(err);
          }
        });
      }
    });

    return sectionErrors;
  };

  update = (queue) => {
    this.props.onUpdate(this.storeKey, {
      List: this.props.List,
      SummaryComments: this.props.SummaryComments,
      ...queue,
    }, [RELATIONSHIPS_STATUS_MARITAL]);
  };

  updateList = (values) => {
    this.update({
      List: values,
    });
  };

  updateSummaryComments = (values) => {
    this.update({
      SummaryComments: values,
    });
  };

  /**
   * Assists in rendering the summary section.
   */
  summary = (item, index) => {
    const o = (item || {}).Item || {};
    const relation = (o.Relation?.value ? o.Relation?.value : i18n.t('relationships.relatives.collection.summary.item')
    );

    // if the relative is unknown, then don't display NBISDNK prefill codes in the Accordion title
    // Do not remove the NBISDNK check below until at least Sept 2022 to ensure all active applications
    // have been processsed which might have NBISDNK entries in them
    const name = o.Name
      && (
        (
          o?.Name.first === i18n.t('relationships.relatives.prefill.name.first')
          && o?.Name.last === i18n.t('relationships.relatives.prefill.name.last')
        )
        || (!o.Name.first && !o.Name.last)
      )
      ? `Unknown ${relation}`
      : NameSummary(o.Name);

    return Summary({
      type: relation,
      index,
      left: name,
      right: null,
      placeholder: i18n.t('relationships.relatives.collection.summary.unknown'),
    });
  };

  getParentErrorMessages = () => {
    const filteredParentInfo = getFilteredParentInfo(this.props.List?.items);
    const parentErrors = [];
    for (const [key, obj] of Object.entries(filteredParentInfo)) {
      const count = obj.fieldPosition.length;
      if (count > 1 && obj.includesIDK) {
        parentErrors.push(
          <ErrorMessage
            title="Duplicate Relatives Information"
            message={[
              `${key} cannot be listed more than once and contain an 'I don't know' selection.`,
              `Please remove the following entry, or uncheck 'I don't know' and insert data:`,
              ...obj.idkPosition.map((index) => ` - Relative Item ${index + 1}`)
            ]}
          />
        );
      }
    }
    return parentErrors;
  };

  render() {
    const {
      List,
      peopleWhoKnowYou,
      requireRelationshipRelativesForeignBornDoc,
      requireRelationshipRelativesUSResidenceDoc,
      requireRelationshipRelativesForeignGovtAffExplanation,
      requireRelationshipRelativesAdditionalFamily,
      requireRelationshipRelativesAPOFPOIDK,
      errors,
      formType,
    } = this.props;

    const accordionErrors = errors && errors.filter((e) => e.indexOf('List.accordion') === 0);
    const parentItemsErrors = this.getParentErrorMessages();
    const duplicatePeopleWhoKnowYou = getDuplicateNamesAndAddresses(List.items, peopleWhoKnowYou, relativesAddressNameFields);

    const duplicatePeopleWhoKnowYouError = (
      <Field
        className="no-margin-bottom"
        title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.title')}
        scrollIntoView={this.props.scrollIntoView}
        hideTitle
      >
        <ErrorMessage
          title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.title')}
          message={[
            i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.message'),
            ...duplicatePeopleWhoKnowYou.errors.map((item) => ` - ${item}`)
          ]}
        />
      </Field>
    );

    const duplicatePeopleWhoKnowYouWarning = (
      <ErrorMessage
        isWarning
        title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouWarning.title')}
        message={[
          i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouWarning.message'),
          ...duplicatePeopleWhoKnowYou.warnings.map((item) => ` - ${item}`)
        ]}
      />
    );
    const isPvq = isPVQForm(formType);
    return isPvq ? (
      <div
        className="section-content relatives"
        data-section={RELATIONSHIPS.key}
        data-subsection={RELATIONSHIPS_RELATIVES.key}
      >
        <h1 className="section-header">{i18n.t('relationships.pvq.relatives.sectionTitle.title')}</h1>

        <Field
          title={i18n.t('relationships.pvq.relatives.heading.title')}
          titleSize="h4"
          optional
          className="no-margin-bottom"
        />

        {List.branch && List.branch.value === 'No' && (
          <ErrorMessageList errors={this.getSectionWarnings()} isWarning />
        )}

        <Accordion
          {...this.props.List}
          defaultState={this.props.defaultState}
          scrollToBottom={this.props.scrollToBottom}
          onUpdate={this.updateList} // eslint-disable-line @typescript-eslint/unbound-method
          onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
          summary={this.summary} // eslint-disable-line @typescript-eslint/unbound-method
          description={i18n.t('relationships.pvq.relatives.collection.summary.title')}
          required={this.props.required}
          errors={accordionErrors}
          scrollIntoView={this.props.scrollIntoView}
          appendTitle={i18n.t('relationships.pvq.relatives.collection.appendTitle')}
          appendLabel={i18n.t('relationships.pvq.relatives.collection.append')}
          additionalErrors={
            (parentItemsErrors.length > 0 && parentItemsErrors)
            || (duplicatePeopleWhoKnowYou.errors.length > 0 && duplicatePeopleWhoKnowYouError)
            || (duplicatePeopleWhoKnowYou.warnings.length > 0 && duplicatePeopleWhoKnowYouWarning)
          }
          ignoreInitialScrollTo
        >
          <Relative
            name="Item"
            applicantBirthdate={this.props.applicantBirthdate}
            addressBooks={this.props.addressBooks}
            dispatch={this.props.dispatch}
            formType={this.props.formType}
            bind
            maritalStatus={this.props.maritalStatus}
            scrollIntoView={this.props.scrollIntoView}
            required={this.props.required}
            requireRelationshipRelativesForeignBornDoc={requireRelationshipRelativesForeignBornDoc}
            requireRelationshipRelativesForeignGovtAffExplanation={
              requireRelationshipRelativesForeignGovtAffExplanation
            }
            requireRelationshipRelativesUSResidenceDoc={requireRelationshipRelativesUSResidenceDoc}
            requireRelationshipRelativesAdditionalFamily={
              requireRelationshipRelativesAdditionalFamily
            }
            requireRelationshipRelativesAPOFPOIDK={requireRelationshipRelativesAPOFPOIDK}
          />
        </Accordion>
      </div>
    ) : (
      <div
        className="section-content relatives"
        data-section={RELATIONSHIPS.key}
        data-subsection={RELATIONSHIPS_RELATIVES.key}
      >
        <h1 className="section-header">{i18n.t('relationships.relatives.sectionTitle.title')}</h1>

        <Field
          title={i18n.t('relationships.relatives.heading.title')}
          titleSize="h3"
          optional
          className="no-margin-bottom"
        >
          {i18n.m('relationships.relatives.para.opportunity')}
        </Field>

        {List.branch && List.branch.value === 'No' && (
        <ErrorMessageList errors={this.getSectionWarnings()} isWarning />
        )}

        <Accordion
          {...this.props.List}
          defaultState={this.props.defaultState}
          scrollToBottom={this.props.scrollToBottom}
          onUpdate={this.updateList} // eslint-disable-line @typescript-eslint/unbound-method
          onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
          summary={this.summary} // eslint-disable-line @typescript-eslint/unbound-method
          description={i18n.t('relationships.relatives.collection.summary.title')}
          required={this.props.required}
          errors={accordionErrors}
          scrollIntoView={this.props.scrollIntoView}
          appendTitle={i18n.t('relationships.relatives.collection.appendTitle')}
          appendLabel={i18n.t('relationships.relatives.collection.append')}
          additionalErrors={
              (parentItemsErrors.length > 0 && parentItemsErrors)
              || (duplicatePeopleWhoKnowYou.errors.length > 0 && duplicatePeopleWhoKnowYouError)
              || (duplicatePeopleWhoKnowYou.warnings.length > 0 && duplicatePeopleWhoKnowYouWarning)
            }
          ignoreInitialScrollTo
        >
          <Relative
            name="Item"
            applicantBirthdate={this.props.applicantBirthdate}
            addressBooks={this.props.addressBooks}
            dispatch={this.props.dispatch}
            bind
            maritalStatus={this.props.maritalStatus}
            scrollIntoView={this.props.scrollIntoView}
            required={this.props.required}
            requireRelationshipRelativesForeignBornDoc={requireRelationshipRelativesForeignBornDoc}
            requireRelationshipRelativesForeignGovtAffExplanation={
                requireRelationshipRelativesForeignGovtAffExplanation
              }
            requireRelationshipRelativesUSResidenceDoc={requireRelationshipRelativesUSResidenceDoc}
            requireRelationshipRelativesAdditionalFamily={
                requireRelationshipRelativesAdditionalFamily
              }
            requireRelationshipRelativesAPOFPOIDK={requireRelationshipRelativesAPOFPOIDK}
          />
        </Accordion>

        {List.branch && List.items?.length > 1 && (
        <Comments
          title={i18n.t('relationships.comments.optional')}
          name="SummaryComments"
          className="optional-comment"
          attribute="SummaryComments"
          {...this.props.SummaryComments}
          onUpdate={this.updateSummaryComments}
          required={false}
        />
        )}
      </div>
    );
  }
}

Relatives.defaultProps = {
  List: {},
  SummaryComments: {},
  onUpdate: () => { },
  onError: (value, arr) => arr,
  section: 'relationships',
  subsection: 'relatives',
  addressBooks: {},
  dispatch: () => { },
  defaultState: true,
  scrollToBottom: '.bottom-btns',
  scrollIntoView: false,
  errors: [],
};

export default connectSubsection(Relatives, sectionConfig);
